import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Bookmark, BookmarkX, Bell, BellOff, Loader2, CalendarClock } from 'lucide-react';
import { watchlistApi } from '../lib/api.js';
import { useTimezone } from '../hooks/useTimezone.js';
import { useAuthStore, useUiStore, usePlayerStore } from '../lib/stores.js';
import { formatTime } from '../lib/time.js';
import styles from './WatchlistPage.module.css';

export default function WatchlistPage() {
  const token = useAuthStore((s) => s.token);
  const openModal = useUiStore((s) => s.openModal);
  const selectChannel = usePlayerStore((s) => s.selectChannel);
  const navigate = useNavigate();
  const tz = useTimezone();
  const qc = useQueryClient();

  const watchlistQ = useQuery({
    queryKey: ['watchlist'],
    queryFn: watchlistApi.list,
    enabled: !!token,
    refetchInterval: 60_000,
  });

  const remove = useMutation({
    mutationFn: (itemId) => watchlistApi.remove(itemId),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['watchlist'] }),
  });
  const toggleAutoSwitch = useMutation({
    mutationFn: ({ itemId, autoSwitch }) => watchlistApi.update(itemId, { auto_switch: autoSwitch }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['watchlist'] }),
  });

  // Group items by day so the day's plan is easy to scan.
  const grouped = useMemo(() => {
    const groups = new Map();
    (watchlistQ.data || []).forEach((item) => {
      const d = new Date(item.program.start_at);
      const key = d.toLocaleDateString([], { weekday: 'long', month: 'short', day: 'numeric' });
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key).push(item);
    });
    return Array.from(groups.entries());
  }, [watchlistQ.data]);

  if (!token) {
    return (
      <div className={styles.center}>
        <Bookmark size={36} />
        <h2>Sign in to build your watchlist</h2>
        <button className="btn primary" onClick={() => openModal('login')}>Sign in</button>
      </div>
    );
  }

  if (watchlistQ.isLoading) return <div className={styles.center}><Loader2 className={styles.spin} /></div>;

  const items = watchlistQ.data || [];

  if (items.length === 0) {
    return (
      <div className={styles.center}>
        <CalendarClock size={36} />
        <h2>Your watchlist is empty</h2>
        <p className="muted">
          Open any channel, browse its schedule, and tap the bookmark on a program to add it here.
        </p>
        <button className="btn primary" onClick={() => navigate('/')}>Browse channels</button>
      </div>
    );
  }

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <h1><Bookmark size={22} /> Watchlist</h1>
        <p className="muted">
          {items.length} saved {items.length === 1 ? 'program' : 'programs'}. Toggle auto-switch to make the player jump
          to the channel when a program starts.
        </p>
      </header>

      {grouped.map(([day, group]) => (
        <section key={day} className={styles.group}>
          <h2 className={styles.groupTitle}>{day}</h2>
          <div className={styles.list}>
            {group.map((item) => (
              <article key={item.id} className={`${styles.card} ${item.auto_switch ? styles.cardOn : ''}`}>
                <div className={styles.cardTime}>
                  <strong>{formatTime(item.program.start_at, tz)}</strong>
                  <span>{formatTime(item.program.end_at, tz)}</span>
                </div>
                <div className={styles.cardLogo}>
                  {item.channel?.logo_url
                    ? <img src={item.channel.logo_url} alt="" />
                    : <span>{(item.channel?.name || '?').slice(0, 2).toUpperCase()}</span>}
                </div>
                <div className={styles.cardBody}>
                  <div className={styles.cardChannel}>{item.channel?.name}</div>
                  <div className={styles.cardTitle}>{item.program.title}</div>
                  {item.program.description && (
                    <div className={styles.cardDesc}>{item.program.description}</div>
                  )}
                </div>
                <div className={styles.cardActions}>
                  <button
                    type="button"
                    className={`btn ${item.auto_switch ? 'primary' : ''}`}
                    onClick={() => toggleAutoSwitch.mutate({ itemId: item.id, autoSwitch: !item.auto_switch })}
                    disabled={toggleAutoSwitch.isPending}
                    title="Switch the player to this channel when the program starts"
                  >
                    {item.auto_switch ? <><Bell size={14} /> Auto-switch on</> : <><BellOff size={14} /> Notify only</>}
                  </button>
                  <button
                    type="button"
                    className="btn ghost"
                    onClick={() => {
                      if (!item.channel?.id) return;
                      selectChannel({
                        id: item.channel.id,
                        name: item.channel.name,
                        slug: item.channel.slug,
                        logo_url: item.channel.logo_url,
                      });
                      navigate('/');
                    }}
                  >
                    Watch channel
                  </button>
                  <button
                    type="button"
                    className={styles.removeBtn}
                    aria-label="Remove from watchlist"
                    onClick={() => remove.mutate(item.id)}
                    disabled={remove.isPending}
                  >
                    <BookmarkX size={16} />
                  </button>
                </div>
              </article>
            ))}
          </div>
        </section>
      ))}
    </div>
  );
}
